// Mute, camera, and device selection — shared by the inspector and the guest.
//
// One component for both because the controls are the same job in both places,
// and because feedback is the kind of problem where whoever notices it needs
// the fix in front of them, not on the other person's screen.

import { useCallback, useEffect, useState } from 'react';
import type { DeviceOption, LiveSwitchCall } from '../liveswitch/call';

type Props = {
  call: LiveSwitchCall | null;
  /** Compact layout for the guest's phone. */
  compact?: boolean;
  /** Shown only where asking makes sense - the inspector's side. */
  onRequestSwap?: () => void;
  onLeave: () => void;
};

export function MediaControls({ call, compact, onRequestSwap, onLeave }: Props) {
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [inputs, setInputs] = useState<DeviceOption[]>([]);
  const [outputs, setOutputs] = useState<DeviceOption[]>([]);
  const [inputId, setInputId] = useState('');
  const [outputId, setOutputId] = useState('');
  const [open, setOpen] = useState(false);
  const [cameraCount, setCameraCount] = useState(0);
  const [swapping, setSwapping] = useState(false);
  const [asked, setAsked] = useState(false);
  const [swapNote, setSwapNote] = useState<string | null>(null);

  // Device labels are only populated once permission has been granted, so this
  // runs after the call is up rather than on mount. Outputs are re-read when
  // the picker is opened because they depend on a remote participant existing.
  const refresh = useCallback(async () => {
    if (!call) return;
    setInputs(await call.audioInputs());
    setOutputs(await call.audioOutputs());
    setCameraCount((await call.videoInputs()).length);
  }, [call]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    call?.setAudioMuted(next);
  };

  const toggleCamera = () => {
    const next = !cameraOff;
    setCameraOff(next);
    call?.setVideoMuted(next);
  };

  return (
    <div className={`mediactl${compact ? ' mediactl--compact' : ''}`}>
      <button
        type="button"
        className={`mediactl__btn${muted ? ' is-off' : ''}`}
        aria-pressed={muted}
        onClick={toggleMute}
      >
        {muted ? 'Unmute' : 'Mute'}
      </button>

      <button
        type="button"
        className={`mediactl__btn${cameraOff ? ' is-off' : ''}`}
        aria-pressed={cameraOff}
        onClick={toggleCamera}
      >
        {cameraOff ? 'Camera on' : 'Camera off'}
      </button>

      {/* Only offered where there is a second camera to move to. On a laptop
          with one webcam the button would be a dead end. */}
      {cameraCount > 1 && (
        <button
          type="button"
          className="mediactl__btn"
          disabled={swapping}
          onClick={() => {
            setSwapping(true);
            call
              ?.swapCamera()
              .then((label) => setSwapNote(label ? null : 'No other camera found.'))
              // Show the real message. Replacing it with a fixed string is why
              // the two buttons looked like two different bugs.
              .catch((err) =>
                setSwapNote(err instanceof Error ? err.message : 'Could not switch camera.'),
              )
              .finally(() => setSwapping(false));
          }}
        >
          {swapping ? 'Switching…' : 'Flip camera'}
        </button>
      )}

      {onRequestSwap && (
        <button
          type="button"
          className="mediactl__btn"
          onClick={() => {
            onRequestSwap();
            setAsked(true);
            setTimeout(() => setAsked(false), 3000);
          }}
        >
          {asked ? 'Asked' : 'Ask to flip camera'}
        </button>
      )}

      <button
        type="button"
        className="mediactl__btn"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
          void refresh();
        }}
      >
        Audio
      </button>

      <button type="button" className="mediactl__btn mediactl__btn--leave" onClick={onLeave}>
        Leave
      </button>

      {swapNote && <span className="mediactl__note">{swapNote}</span>}

      {open && (
        <div className="mediactl__panel">
          <label className="mediactl__field">
            <span className="eyebrow">Microphone</span>
            <select
              value={inputId}
              onChange={(e) => {
                setInputId(e.target.value);
                void call?.setAudioInput(e.target.value);
              }}
            >
              <option value="">System default</option>
              {inputs.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.label || 'Microphone'}
                </option>
              ))}
            </select>
          </label>

          {/* Hidden where the browser has no setSinkId — Firefox and iOS
              Safari — rather than offered as a control that does nothing. */}
          {outputs.length > 0 && (
            <label className="mediactl__field">
              <span className="eyebrow">Speaker</span>
              <select
                value={outputId}
                onChange={(e) => {
                  setOutputId(e.target.value);
                  void call?.setAudioOutput(e.target.value);
                }}
              >
                <option value="">System default</option>
                {outputs.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.label || 'Speaker'}
                  </option>
                ))}
              </select>
            </label>
          )}

          <p className="mediactl__hint">
            Hearing an echo? Use headphones, or mute while the other person is talking. Two
            devices in one room will always feed back.
          </p>
        </div>
      )}
    </div>
  );
}
